import { useEffect } from 'react';

/**
 * Mirror the unread-mail count onto the installed app's icon (Badging API).
 *
 * The service worker sets the badge while the app is closed (from the push
 * payload); this keeps it honest while the app is open - the count drops as mail
 * is read and is cleared when it reaches zero. Feature-detected throughout:
 * iOS Safari in a tab, Firefox and most desktop browsers have no Badging API,
 * and the calls can reject (no notification permission on iOS) - none of that
 * may surface as an error.
 */

/** Matches the cap the push server applies, so the icon does not flip between
 *  "99" from a push and "143" from the open app. */
export const MAX_APP_BADGE = 99;

/** Whole number in 0..MAX_APP_BADGE; anything non-numeric counts as 0. */
export function clampBadgeCount(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(Math.floor(n), MAX_APP_BADGE);
}

export interface BadgeNavigator {
  setAppBadge?: (contents?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
}

/** Set (or clear, at 0) the app badge. Returns false when the API is absent or
 *  throws synchronously; async rejections are swallowed. */
export function applyAppBadge(
  count: unknown,
  nav: BadgeNavigator | undefined = typeof navigator !== 'undefined' ? (navigator as BadgeNavigator) : undefined,
): boolean {
  if (!nav) return false;
  const n = clampBadgeCount(count);
  try {
    if (n === 0) {
      if (typeof nav.clearAppBadge !== 'function') return false;
      Promise.resolve(nav.clearAppBadge()).catch(() => undefined);
      return true;
    }
    if (typeof nav.setAppBadge !== 'function') return false;
    Promise.resolve(nav.setAppBadge(n)).catch(() => undefined);
    return true;
  } catch {
    return false;
  }
}

/** How long a count of 0 must hold before it is written to the icon. */
export const ZERO_BADGE_SETTLE_MS = 300;

/**
 * @param count  unread inbox count (EmailPanelContext.unreadBadgeCount)
 * @param ready  false until the first mail snapshot has landed - the context
 *               reports 0 before then, and writing that would wipe the badge the
 *               service worker just set.
 *
 * `ready` alone is not enough: the context derives the count in an effect, one
 * render AFTER the snapshot lands, so the first `ready === true` commit can still
 * carry the pre-snapshot 0 before the real N arrives a render later. A 0 is
 * therefore only written once it has held for ZERO_BADGE_SETTLE_MS - a transient
 * 0 is cancelled by the next count and never reaches the icon (no blink, and no
 * clear that a rejected follow-up set could leave stuck). Positive counts are
 * applied immediately.
 */
export function useAppBadge(count: number, ready: boolean = true): void {
  useEffect(() => {
    if (!ready) return;
    if (clampBadgeCount(count) > 0) {
      applyAppBadge(count);
      return;
    }
    const timer = setTimeout(() => { applyAppBadge(0); }, ZERO_BADGE_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [count, ready]);

  // Signing out unmounts the mail panel: do not leave the previous user's
  // count on the icon. Immediate - the settle delay above is for transient
  // zeros, and its pending timer is cancelled by that effect's own cleanup.
  useEffect(() => {
    return () => { applyAppBadge(0); };
  }, []);
}
